import { useCallback, useEffect, useRef, useState } from 'react';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { genUUIDV4 } from '@frontend/string';
import { pendo } from '@frontend/tracking';
import {
  CollectPaymentMultiActiveGateway,
  CollectPaymentMultiFlow,
  CollectPaymentMultiSteps,
} from '../collect-payment-multi.types';

const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

/**
 * Use the following script to see output in the console:
 * localStorage.setItem('debug-web', 'true');
 *
 * Dev warn: Shows when step registerations are aborted (probably correctly) based on current state.
 * Dev log: Shows the pendo payloads that are sent.
 */
export const devWarn = (...args: any[]) => {
  if (isDev || localStorage.getItem('debug-web') === 'true') {
    console.warn(...args);
  }
};

const devLog = (...args: any[]) => {
  if (localStorage.getItem('debug-web') === 'true') {
    console.log(...args);
  }
};

type MetaDataProgress = 'open' | 'complete' | 'cancel';
export type MetaDataStepVariant<T extends string> = T extends 'card-on-file'
  ? 'card-on-file:save-card' | 'card-on-file:save-ach' | 'card-on-file:complete-payment'
  : 'default';

/**
 * Official step data tracking type
 * Use for function props when we care about getting step + variant correct.
 */
type PendoCollectionStepData<T extends CollectPaymentMultiSteps | 'start-to-end'> = {
  initialTimeStamp: number;
  screenWidth: number;
  progress: MetaDataProgress;
  step: T;
  flow: CollectPaymentMultiFlow;
  variant: MetaDataStepVariant<T>;
  activeGateway: CollectPaymentMultiActiveGateway;
  uuid: string;
};

/**
 * Use internally in store only
 * Use when we're adding, removing tracking data and we don't care about step + variant specificity
 */
type PendoCollectionStepdataGeneric = PendoCollectionStepData<CollectPaymentMultiSteps>;

type PendoCollectionStepEventMetadata<T extends string> = {
  secondsElapsed: number;
  screenWidth: number;
  variant: MetaDataStepVariant<T>;
  step: T;
  flow: CollectPaymentMultiFlow;
  activeGateway: CollectPaymentMultiActiveGateway;
  progress: MetaDataProgress;
};

type PendoCollectionModalState = {
  uuid: string;
  initialTimestamp: number;
  hasSent: boolean;
  screenWidth: number;
  flow: CollectPaymentMultiFlow;
  activeGateway: CollectPaymentMultiActiveGateway;
  firstStep: CollectPaymentMultiSteps;
  status: MetaDataProgress;
  paymentMethod: PendoCollectionModalPaymentMethods | null;
  createdInvoice: boolean;
  paymentComplete: boolean;
};

type PendoCollectionCompleteMetadata = Omit<PendoCollectionModalState, 'initialTimestamp' | 'hasSent'> & {
  totalSecondsElapsed: number;
};

type PendoCollectionModalPaymentMethods = 'collect-reader-payment' | 'card-on-file' | 'manual-card-entry';

export type PendoCollectionModalInitializeProps = {
  uuid: string;
  flow: CollectPaymentMultiFlow;
  activeGateway: CollectPaymentMultiActiveGateway;
  firstStep: CollectPaymentMultiSteps;
  screenWidth?: number;
};

type ReportPendoCollectionStepEventProps<T extends CollectPaymentMultiSteps | 'start-to-end'> =
  PendoCollectionStepData<T>;

type PendoCollectionModalFinalizeProps = {
  status: 'complete' | 'cancel';
};

type PendingPromises = { uuid: string; promise: Promise<void>; resolve: () => void }[];

type CompleteStepTrackingProps<T extends CollectPaymentMultiSteps> = Pick<
  PendoCollectionStepData<T>,
  'step' | 'variant'
> & {
  withIntentToClose?: boolean;
};

export type InitializeStepTrackingProps<T extends CollectPaymentMultiSteps> = Pick<
  PendoCollectionStepData<T>,
  'activeGateway' | 'variant' | 'screenWidth' | 'step' | 'flow'
>;

type UpdateModalStateProps = Partial<
  Pick<
    PendoCollectionModalState,
    'hasSent' | 'status' | 'activeGateway' | 'paymentComplete' | 'paymentMethod' | 'createdInvoice' | 'flow'
  >
>;

const BASE_EVENT_NAME = 'payments:multi-collection-modal';

export type StepTrackingStore = {
  initiateStepTracking: <T extends CollectPaymentMultiSteps>(props: InitializeStepTrackingProps<T>) => void;
  cancelStepTracking: <T extends CollectPaymentMultiSteps>(
    props: Pick<PendoCollectionStepData<T>, 'step' | 'variant'>
  ) => void;
  completeStepTracking: <T extends CollectPaymentMultiSteps>(props: CompleteStepTrackingProps<T>) => void;
  initiateModalState: (props: PendoCollectionModalInitializeProps) => void;
  finalizeModalState: (props: PendoCollectionModalFinalizeProps) => Promise<void>;
  updateModalState: (props: UpdateModalStateProps) => void;
  trackingSteps: Partial<Record<string, PendoCollectionStepdataGeneric>>;
  modalState: PendoCollectionModalState | Record<string, never>;
  pendingPromises: PendingPromises;
};

const makePendoEventName = <T extends CollectPaymentMultiSteps | 'start-to-end'>({
  step,
  activeGateway,
}: Pick<PendoCollectionStepData<T>, 'activeGateway' | 'step'>) => {
  return `${BASE_EVENT_NAME}:${activeGateway}:${step}`;
};

const makeStepKey = <T extends CollectPaymentMultiSteps>(
  step: T,
  variant: MetaDataStepVariant<T> | 'default' = 'default'
) => {
  return `${step}:${variant}`;
};
const reportPendoCollectionStepEvent = <T extends CollectPaymentMultiSteps | 'start-to-end'>({
  initialTimeStamp,
  screenWidth,
  progress,
  step,
  variant,
  flow,
  activeGateway,
}: ReportPendoCollectionStepEventProps<T>) => {
  const metadata: PendoCollectionStepEventMetadata<T> = {
    secondsElapsed: parseFloat(((Date.now() - initialTimeStamp) / 1000).toFixed(1)),
    screenWidth,
    variant,
    step,
    progress: progress === 'open' ? 'cancel' : progress,
    flow,
    activeGateway,
  };

  const eventName = makePendoEventName({ step, activeGateway });

  devLog('reportPendoCollectionStepEvent', eventName, metadata);

  pendo.track(eventName, metadata);
};

const reportPendoCollectionFinalizedEvent = ({
  initialTimestamp,
  hasSent: _hasSent,
  ...props
}: PendoCollectionModalState) => {
  const metadata: PendoCollectionCompleteMetadata = {
    ...props,
    totalSecondsElapsed: parseFloat(((Date.now() - initialTimestamp) / 1000).toFixed(1)),
  };

  const eventName = makePendoEventName({
    activeGateway: props.activeGateway,
    step: 'start-to-end',
  });

  devLog('reportPendoCollectionFinalizedEvent', eventName, metadata);

  pendo.track(eventName, metadata);
};

const validateStepDataForReport = (
  data: PendoCollectionStepdataGeneric | undefined
): data is PendoCollectionStepdataGeneric => {
  if (!data) {
    devWarn('No step data provided for reporting');
    return false;
  }

  if (!data.step) {
    devWarn('Step is required for reporting');
    return false;
  }

  if (!data.activeGateway) {
    devWarn('Active gateway is required for reporting');
    return false;
  }

  if (!data.initialTimeStamp) {
    devWarn('Initial timestamp is required for reporting');
    return false;
  }

  if (!data.screenWidth) {
    devWarn('Screen width is required for reporting');
    return false;
  }

  if (!data.progress) {
    devWarn('Progress is required for reporting');
    return false;
  }

  return true;
};

const hasExistingModalState = (
  modalState: StepTrackingStore['modalState']
): modalState is PendoCollectionModalState => {
  return !!modalState && (!!modalState.status || modalState.hasSent) && !!modalState.uuid;
};

const canInitializeModalState = (
  modalState: StepTrackingStore['modalState'],
  toSet: PendoCollectionModalInitializeProps
) => {
  if (hasExistingModalState(modalState)) {
    if (modalState.uuid === toSet.uuid && !modalState.hasSent) {
      // same modal, do not reset -- you'll reset count
      return false;
    }
  }

  return true;
};

const createResolveablePromise = (): { promise: Promise<void>; resolve: () => void } => {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((res) => {
    resolve = res;
  });

  return { promise, resolve };
};

const isPaymentStep = (step: CollectPaymentMultiSteps): step is PendoCollectionModalPaymentMethods => {
  return ['manual-card-entry', 'collect-reader-payment', 'card-on-file'].includes(step);
};

const isCreateStep = (step: CollectPaymentMultiSteps): boolean => {
  return ['create-invoice'].includes(step);
};

export const useStepTrackingStore = createStoreWithSubscribe<StepTrackingStore>(
  (set, get) => {
    const resolvePromiseByUuid = (uuid: string) => {
      const found = get().pendingPromises.find((p) => p.uuid === uuid);
      if (found) {
        found.resolve();
      }
    };

    const clearPromises = () => {
      set((state) => {
        state.pendingPromises = [];
      });
    };

    const awaitAllPendingPromises = async () => {
      await Promise.all(get().pendingPromises.map((p) => p.promise));
    };

    const updateModalState = (toSet: UpdateModalStateProps) => {
      set((state) => {
        if (hasExistingModalState(state.modalState)) {
          state.modalState = { ...state.modalState, ...toSet };
        }
      });
    };

    const resetModalState = () => {
      set((state) => {
        state.modalState = {};
      });
    };

    const commitFinalizeModalState: StepTrackingStore['finalizeModalState'] = async ({ status }) => {
      set((state) => {
        const currentStatus = state.modalState.status;

        /**
         * Modal cancel might have been called, but a step like terminal completed.
         */

        if (currentStatus !== 'complete') {
          state.modalState.status = status;
        }

        if (!hasExistingModalState(state.modalState)) {
          devWarn('No existing modal state to close');
          return;
        } else if (state.modalState.hasSent) {
          devWarn('Modal state already finalized and sent');
          return;
        }

        reportPendoCollectionFinalizedEvent(state.modalState);

        state.modalState.hasSent = true;
      });
    };

    const updateRegisteredStep = <T extends CollectPaymentMultiSteps>(
      props: Partial<PendoCollectionStepData<T>> & Pick<PendoCollectionStepData<T>, 'step' | 'variant'>
    ) => {
      set((state) => {
        if (!props.step) {
          return;
        }
        const stepKey = makeStepKey(props.step, props.variant);
        const currentStepData = state.trackingSteps[stepKey];

        if (currentStepData?.uuid) {
          state.trackingSteps[stepKey] = {
            ...currentStepData,
            activeGateway: props.activeGateway || currentStepData?.activeGateway,
            flow: props.flow || currentStepData?.flow,
            screenWidth: props.screenWidth || currentStepData?.screenWidth,
            progress:
              /**
               * Once a step progress has been updated, don't change it.
               */
              currentStepData?.progress === 'open'
                ? props.progress || currentStepData.progress
                : currentStepData?.progress,
          };
        }
      });
    };

    const registerStep = <T extends CollectPaymentMultiSteps>(props: InitializeStepTrackingProps<T>) => {
      set((state) => {
        const uuid = genUUIDV4();
        const { promise, resolve } = createResolveablePromise();
        const stepKey = makeStepKey(props.step, props.variant);

        state.pendingPromises.push({ uuid, promise, resolve });
        state.modalState.paymentMethod = isPaymentStep(props.step) ? props.step : null;
        state.trackingSteps[stepKey] = {
          ...props,
          uuid,
          progress: 'open',
          initialTimeStamp: Date.now(),
          screenWidth: props.screenWidth || window.innerWidth,
          variant: props.variant || 'default',
        };
      });
    };

    const removeStepDataByStepKey = (stepKey: string) => {
      set((state) => {
        const newTrackingSteps = { ...state.trackingSteps };
        delete newTrackingSteps[stepKey];

        state.trackingSteps = newTrackingSteps;
      });
    };

    const modalStateHasSent = () => {
      const modalState = get().modalState;

      return modalState.uuid && modalState.hasSent;
    };

    const isModalStateInitialized = () => {
      const modalState = get().modalState;

      return !!(modalState && modalState.uuid);
    };

    const modalIsStatusComplete = () => {
      const modalState = get().modalState;

      return modalState.status === 'complete';
    };

    return {
      updateModalState,
      finalizeModalState: async (props) => {
        let hasFinalized = false;

        const commitAndReset = () => {
          commitFinalizeModalState(props);
          clearPromises();
          resetModalState();
          hasFinalized = true;
        };
        /**
         * Master timeout in case weirdness happens and promises never resolve.
         */
        const handle = setTimeout(() => {
          devWarn('Finalizing modal state due from master timeout -- a step was never resolved');
          if (!hasFinalized) {
            commitAndReset();
          }
        }, 7500);

        awaitAllPendingPromises().then(() => {
          clearTimeout(handle);
          if (!hasFinalized) {
            commitAndReset();
          }
        });
      },
      initiateModalState: (props) => {
        set((state) => {
          const existingState = { ...state.modalState };

          if (!canInitializeModalState(existingState, props) && hasExistingModalState(existingState)) {
            // update some values, not others
            state.modalState = {
              ...existingState,
              flow: props.flow,
              activeGateway: props.activeGateway,
              screenWidth: props.screenWidth || existingState.screenWidth,
            };
          }

          state.modalState = {
            /**
             * Values that should only be set on the initial registration
             */
            uuid: props.uuid,
            status: 'open',
            firstStep: props.firstStep,
            initialTimestamp: Date.now(),
            paymentMethod: null,
            createdInvoice: false,
            paymentComplete: false,
            hasSent: false,

            /**
             * Values that can be updated with subsequent registrations
             */
            flow: props.flow,
            screenWidth: props.screenWidth || window.innerWidth,
            activeGateway: props.activeGateway,
          };
        });
      },
      initiateStepTracking: (props) => {
        const stepKey = makeStepKey(props.step, props.variant);
        const currentStepData = get().trackingSteps[stepKey];

        if (currentStepData?.uuid) {
          updateRegisteredStep(props);
          return;
        } else if (modalStateHasSent()) {
          devWarn('Modal state has already been sent, cannot initiate step tracking');
          return;
        } else if (!isModalStateInitialized()) {
          devWarn('Modal state has already been sent, cannot initiate step tracking');
          return;
        } else if (modalIsStatusComplete()) {
          devWarn('Modal state is complete, cannot initiate step tracking');
          return;
        }

        registerStep(props);
      },
      cancelStepTracking: ({ step, variant }) => {
        /**
         * Track event
         */

        const stepKey = makeStepKey(step, variant);
        const stepData = get().trackingSteps[stepKey];

        if (!validateStepDataForReport(stepData)) {
          return;
        }
        reportPendoCollectionStepEvent(stepData);
        resolvePromiseByUuid(stepData.uuid);
        removeStepDataByStepKey(stepKey);
      },
      completeStepTracking: ({ step, variant, withIntentToClose }) => {
        updateRegisteredStep({ step, variant, progress: 'complete' });

        const stepKey = makeStepKey(step, variant);
        const stepData = get().trackingSteps[stepKey];
        if (!validateStepDataForReport(stepData)) {
          return;
        }

        reportPendoCollectionStepEvent({ ...stepData, progress: 'complete' });
        resolvePromiseByUuid(stepData.uuid);
        removeStepDataByStepKey(stepKey);

        const modalState = get().modalState;

        updateModalState({
          paymentComplete: isPaymentStep(stepData.step) || modalState.paymentComplete, // keep true if already true
          createdInvoice: isCreateStep(stepData.step) || modalState.createdInvoice, // keep true if already true
          status: isPaymentStep(stepData.step) || withIntentToClose ? 'complete' : modalState.status, // keep current status, if not explicitly complete
        });
      },
      trackingSteps: {},
      modalState: {},
      pendingPromises: [],
    };
  },
  { name: 'StepTrackingStore', trace: true }
);

export const useStepTrackingStoreShallow = createShallowStore<StepTrackingStore>(useStepTrackingStore);

export const useInitiateStepTracking = <T extends CollectPaymentMultiSteps>(
  props: InitializeStepTrackingProps<T>,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { initiateStepTracking } = useStepTrackingStoreShallow('initiateStepTracking');

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (enabled) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        initiateStepTracking(props);
      }, 400);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [props, enabled]);
};

export type UseFinalizeStepTrackingCallbacksProps<T extends CollectPaymentMultiSteps> = {
  step: T;
  variant: MetaDataStepVariant<T>;
};

export function useFinalizeStepTrackingCallbacks<T extends CollectPaymentMultiSteps>(
  props: UseFinalizeStepTrackingCallbacksProps<T>
) {
  const hasFinalized = useRef(false);

  const { cancelStepTracking, completeStepTracking, updateModalState } = useStepTrackingStoreShallow(
    'cancelStepTracking',
    'completeStepTracking',
    'updateModalState'
  );
  const completeStepTrackingCallback = useCallback(
    ({ withIntentToClose = false }: { withIntentToClose?: boolean } = {}) => {
      completeStepTracking({ ...props, withIntentToClose });
      hasFinalized.current = true;
    },
    [props, completeStepTracking]
  );

  const cancelStepTrackingCallback = useCallback(() => {
    /**
     * On terminal step, after success, we get a delayed "close click".
     * Prevent a "no tracking data" warning once success has already been called.
     */
    if (hasFinalized.current) {
      return;
    }
    cancelStepTracking(props);
    hasFinalized.current = true;
  }, [props, cancelStepTracking]);

  return {
    completeStepTracking: completeStepTrackingCallback,
    cancelStepTracking: cancelStepTrackingCallback,
    updateModalState,
  };
}
export const useStepTrackingStoreUuid = () => {
  const [state, setState] = useState(genUUIDV4());

  const regenerate = useCallback(() => {
    setState(genUUIDV4());
  }, []);

  return { uuid: state, regenerate };
};
